import { useEffect, useRef } from 'react'
import { formatDayMonth, formatProgramDate } from '@/lib/programs'
import type { Participation } from '@/types/programs'

/**
 * Confirm stopping. Before the start date it cancels the sign-up at once; once
 * active it stops on the next first of the month, can still be taken back until
 * then, and means no reward at all.
 */
export function OptOutDialog({
  participation,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  participation: Participation
  busy: boolean
  error: string
  onConfirm: () => void
  onCancel: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
    return () => dialog?.close()
  }, [])

  const beforeStart = participation.status === 'pending'
  const effective = participation.optOutEffectiveDate ?? participation.program.endsAt
  const title = beforeStart ? 'Aanmelding annuleren?' : 'Weet je zeker dat je wilt stoppen?'
  const bullets = beforeStart
    ? [
        'Je aanmelding wordt meteen geannuleerd.',
        `Je kunt je tot ${formatDayMonth(participation.startDate)} opnieuw aanmelden.`,
      ]
    : [
        `Je deelname stopt per ${formatProgramDate(effective)}.`,
        'Je ontvangt geen beloning, ook niet voor de maanden die je al meedeed.',
        `Tot ${formatDayMonth(effective)} kun je dit nog terugdraaien.`,
      ]

  return (
    <dialog
      ref={dialogRef}
      onCancel={(e) => {
        e.preventDefault()
        if (!busy) onCancel()
      }}
      className="w-[calc(100%-32px)] max-w-[480px] rounded-3xl p-0 text-dark-blue shadow-2xl backdrop:bg-dark-blue/45"
      aria-labelledby="opt-out-title"
    >
      <div className="p-6 md:p-8">
        <h2 id="opt-out-title" className="text-xl font-bold lg:text-26">
          {title}
        </h2>
        <ul className="mt-4 flex flex-col gap-2.5">
          {bullets.map((b) => (
            <li key={b} className="flex gap-3 text-15 leading-normal text-dark-blue/80">
              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-red" aria-hidden="true" />
              {b}
            </li>
          ))}
        </ul>
        {error && <p className="mt-4 text-15 font-semibold text-red">{error}</p>}
        <div className="mt-6 flex flex-col gap-3">
          <button type="button" className="btn-danger w-full" onClick={onConfirm} disabled={busy}>
            {busy ? 'Bezig…' : beforeStart ? 'Ja, annuleer mijn aanmelding' : `Ja, stop per ${formatDayMonth(effective)}`}
          </button>
          <button type="button" className="btn-secondary w-full" onClick={onCancel} disabled={busy}>
            Blijf meedoen
          </button>
        </div>
      </div>
    </dialog>
  )
}
